import type { AssistantStreamEvent } from '../models/assistant';

export class AssistantStreamParser {
  private offset = 0;
  private buffer = '';
  finished = false;

  private readonly messageId: string;

  constructor(messageId: string) {
    this.messageId = messageId;
  }

  read(text: string): AssistantStreamEvent[] {
    this.buffer += text.slice(this.offset);
    this.offset = text.length;
    const events: AssistantStreamEvent[] = [];
    let boundary: RegExpExecArray | null;
    while ((boundary = /\r?\n\r?\n/.exec(this.buffer)) !== null) {
      const frame = this.buffer.slice(0, boundary.index);
      this.buffer = this.buffer.slice(boundary.index + boundary[0].length);
      const data = frame
        .split(/\r?\n/)
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n');
      if (!data) {
        continue;
      }
      if (this.finished) {
        throw new Error('Received an event after the assistant stream finished.');
      }
      const event: unknown = JSON.parse(data);
      if (!this.isEvent(event)) {
        throw new Error('The assistant returned an invalid stream event.');
      }
      events.push(event);
      this.finished = event.type === 'completed' || event.type === 'error';
    }
    return events;
  }

  assertFinished(): void {
    if (!this.finished || this.buffer.trim()) {
      throw new Error('The assistant stream ended unexpectedly.');
    }
  }

  private isEvent(value: unknown): value is AssistantStreamEvent {
    if (typeof value !== 'object' || value === null || !('messageId' in value) || value.messageId !== this.messageId || !('type' in value)) {
      return false;
    }
    switch (value.type) {
      case 'status':
        return (
          'status' in value &&
          typeof value.status === 'string' &&
          ['preparing', 'thinking', 'retrieving', 'processing', 'writing'].includes(value.status) &&
          (!('foundryConversationId' in value) || value.foundryConversationId === null || typeof value.foundryConversationId === 'string')
        );
      case 'textDelta':
        return 'text' in value && typeof value.text === 'string';
      case 'completed':
        return (
          'text' in value &&
          typeof value.text === 'string' &&
          value.text.trim().length > 0 &&
          'foundryConversationId' in value &&
          typeof value.foundryConversationId === 'string' &&
          value.foundryConversationId.length > 0
        );
      case 'error':
        return 'status' in value && (value.status === 'failed' || value.status === 'timeout');
      default:
        return false;
    }
  }
}
